import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators';

import { Menu } from '@material/mwc-menu';
import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import { getNewUiUrl } from '../../helpers/auth-cookie';
import { getInitials } from '../../helpers/strings';
import { logWithAmplitude } from '../../helpers/api/api';

@customElement('context-menu')
export class ContextMenu extends LitElement {


    @query('#cornerMenu')
    cornerMenu!: Menu;

    @query('#menuConnect')
    menuConnect!: HTMLDivElement;

    @property()
    type?: string = 'admin-portal';

    @property()
    amplitude_callsite: string = '';

    @property({ type: Boolean })
    menu_only: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        this.cornerMenu.anchor = this.menuConnect;
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.type !== 'command-center') {
            this.style.setProperty('--context-menu-text-color', 'var(--main-primary-color)');
        }
    }

    protected render() {
        const xPos = this.menu_only ? 0 : 10;
        const yPos = this.menu_only ? 0 : 48;

        return html`
            <div style="position: relative">
                <div id="menuConnect" class="flexContainer">
                    ${!this.menu_only ? html`
                        ${accountInfo.isAuthenticated
                            ? html`
                                <account-avatar
                                    size="sm"
                                    .imageUrl="${accountInfo.account?.image_url}"
                                    @click=${() => (this.cornerMenu.open = true)}>
                                    ${getInitials(
                                        accountInfo.fullName,
                                        accountInfo.email
                                    )}
                                </account-avatar>
                            `
                            : this.type === 'command-center' ? ''
                            : html` <mwc-icon-button
                                icon="input"
                                @click="${this.signInClick}"
                            ></mwc-icon-button>`}
                    ` : html`<slot></slot>`}
                </div>
                <mwc-menu id="cornerMenu" x="${xPos}" y="${yPos}">
                    <mwc-list-item id="profileListItem" @click="${this.accountPageClick}">
                        <div class="flexContainer">
                            <account-avatar size="sm" @click=${() => (this.cornerMenu.open = true)} .imageUrl="${accountInfo.account?.image_url}">
                                ${getInitials(
                                    accountInfo.fullName,
                                    accountInfo.email
                                )}
                            </account-avatar>
                            <div class="profileDetails">
                                <span class="detailsName">${accountInfo.fullName}</span>
                                <span class="detailsEmail">${accountInfo.email}</span>
                            </div>
                        </div>
                    </mwc-list-item>
                    <mwc-list-item @click="${this.accountPageClick}">
                        <div class="flexContainer">
                            <mwc-icon class="menuIcon">person_outline</mwc-icon>
                            <span>Account Info ${accountInfo.isAppSumoUser === true ? 'and License' : ''}</span>
                        </div>
                    </mwc-list-item>
                    <mwc-list-item @click="${this.backpackClick}">
                        <div class="flexContainer">
                            <mwc-icon class="menuIcon">backpack</mwc-icon>
                            <span>My Backpack (All Maps)</span>
                        </div>
                    </mwc-list-item>
                    <li divider></li>
                    <mwc-list-item @click="${this.logout}">
                        <div class="flexContainer">
                            <mwc-icon class="menuIcon">logout</mwc-icon>
                            <span>Log Out</span>
                        </div>
                    </mwc-list-item>
                </mwc-menu>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                --mdc-menu-min-width: min(100vw, 100px);
                --mdc-menu-item-height: 1.75rem;

                --mdc-typography-subtitle1-font-size: 12px;
            }

            .divider {
                width: 1px;
                height: 1.5rem;
                margin: 0 1rem;
                background: var(--theme-input-color);
            }

            #profileListItem {
                --mdc-menu-item-height: 3.5rem;
            }

            .profileDetails {
                display: flex;
                flex-direction: column;
                margin-left: 0.5rem;
            }

            .detailsName {
                font-size: 0.8rem;
                font-weight: 700;
                line-height: 1.25rem;
                color: var(--theme-input-color);
            }

            .detailsEmail {
                font-size: 0.75rem;
                line-height: 1rem;
                color: var(--theme-input-color);
            }

            .menuIcon {
                margin-right: 0.5rem;
                --mdc-icon-size: 0.8rem;
                color: var(--theme-lighter-text-color);
            }

            #expandProfile {
                --mdc-icon-button-size: 2.5rem;
                color: var(--context-menu-text-color);
                margin-right: -0.5rem;
            }

            .guestViewer {
                color: var(--context-menu-text-color);
                text-align: center;
                cursor: pointer;
                font-weight: bold;
                font-size: 12px;
            }

            .backpackicon {
                height: 1.5rem;
                margin-right: 5px;
            }

            mwc-icon-button {
                color: var(--context-menu-text-color);
                --mdc-icon-button-size: 2.5rem;
            }

            .flexContainer {
                display: flex;
                align-items: center;
                justify-content: center;
            }

            ::slotted(*) {
                cursor: pointer;
            }

            @media screen and (min-width: 901px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }

                .desktopOnly.flexContainer {
                    display: flex;
                }
            }

            @media screen and (max-width: 900px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: flex;
                }

                .mobileOnly.flexContainer {
                    display: flex;
                }

                #expandProfile {
                    --mdc-icon-button-size: 2rem;
                }

                .guestViewer {
                    display: none;
                }
            }
        `;
    }

    private async logout() {
        logWithAmplitude(this.amplitude_callsite, 'User Logout Click');
        await accountInfo.logout();
    }

    private signInClick() {
        logWithAmplitude(this.amplitude_callsite, 'User Login Click');
        this.dispatchEvent(new CustomEvent('on-signin'));
    }

    private accountPageClick() {
        logWithAmplitude(this.amplitude_callsite, 'Account Page Click');
        window.location.assign(`#home?settings=account`);
    }

    private backpackClick() {
        logWithAmplitude(this.amplitude_callsite, 'Backpack Click');
        window.location.assign(`#home`);
    }

    private discoverClick() {
        logWithAmplitude(this.amplitude_callsite, 'Proxi Discover Click');
        window.location.assign(`${getNewUiUrl()}/discover`);
    }

    private manageSubscriptionClick() {
        this.dispatchEvent(new CustomEvent('on-manage-subscription'));
        logWithAmplitude(this.amplitude_callsite, 'Manage Subscription Click');
    }
}
